import { useEffect, useRef, useState } from 'react';
import { DX_SUGGEST_MAP, ICD10_CATALOG, type NoteDiagnosis, type TpPlanSummary } from '@ehr/shared';
import { api } from '../lib/api.js';

/**
 * calendar#46 step 3 — the progress note's "Diagnosis codes (ICD-10)*" card, 1:1 with
 * PROGRESS_NOTE_A-15: two segmented buttons (From Treatment plan / Search diagnosis
 * codes…) that both open the same modal; picks apply immediately and show as chips
 * on the page; the modal closes on ×, a backdrop click or Escape. "From Treatment plan"
 * lists only the client's live plan codes (read from the TP module); "Search" groups
 * codes Suggested for the checked Presenting concerns (Credify Note) above All codes.
 * The codes are the one ICD-10 catalog every picker uses (ICD10_CATALOG, ehr#741 follow-up).
 */

export type DxScope = 'tp' | 'all';
type Row = readonly [string, string];
export interface DxGroup {
  label: string | null;
  rows: Row[];
}

const catalogLabel = new Map(ICD10_CATALOG.map(([c, l]) => [c, l]));
const libLabel = (code: string) => catalogLabel.get(code) ?? '';
const matches =
  (q: string) =>
  ([code, label]: Row) =>
    code.toLowerCase().includes(q) || label.toLowerCase().includes(q);

/**
 * The modal's list, as A-15's renderDxPanel builds it. Exported for tests.
 * tp scope: the plan's codes only (TP label, else the library's), filtered by the query.
 * all scope: with a query, a flat filtered list; without, "Suggested for …" (from the
 * checked Presenting concerns) then "All codes" (the rest). A code already on the note that the
 * catalog doesn't have (saved before, such as F50.2) leads All codes with its saved label, so it
 * still shows ticked and can be unticked.
 */
export function dxGroups(
  scope: DxScope,
  query: string,
  concerns: string[],
  tpDx: { code: string; label: string }[],
  saved: readonly NoteDiagnosis[] = [],
): DxGroup[] {
  const q = query.trim().toLowerCase();
  if (scope === 'tp') {
    const rows: Row[] = tpDx.map((d) => [d.code, d.label || libLabel(d.code)] as const);
    return [{ label: null, rows: q ? rows.filter(matches(q)) : rows }];
  }
  const kept: Row[] = [];
  for (const d of saved)
    if (!catalogLabel.has(d.code) && !kept.some(([c]) => c === d.code))
      kept.push([d.code, d.label] as const);
  const all: Row[] = [...kept, ...ICD10_CATALOG];
  if (q) return [{ label: null, rows: all.filter(matches(q)) }];
  const codes = new Set<string>();
  concerns.forEach((c) => (DX_SUGGEST_MAP[c] ?? []).forEach((code) => codes.add(code)));
  const suggested = ICD10_CATALOG.filter(([code]) => codes.has(code));
  const groups: DxGroup[] = [];
  if (suggested.length)
    groups.push({ label: `Suggested for "${concerns.join(', ')}"`, rows: suggested });
  groups.push({ label: 'All codes', rows: all.filter(([code]) => !codes.has(code)) });
  return groups;
}

export function DiagnosisCodesCard({
  contactId,
  contactName,
  value,
  onChange,
  concerns,
}: {
  contactId: string;
  contactName: string;
  value: NoteDiagnosis[];
  // Updater form, so rapid picks each build on the latest list.
  onChange: (update: (prev: NoteDiagnosis[]) => NoteDiagnosis[]) => void;
  /** Checked Presenting concerns (Credify Note) — drive "Suggested for …". */
  concerns: string[];
}) {
  const [source, setSource] = useState<DxScope>('tp');
  const [scope, setScope] = useState<DxScope | null>(null);
  const [query, setQuery] = useState('');
  const [tp, setTp] = useState<TpPlanSummary | null>(null);
  const [tpLoading, setTpLoading] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // The plan's codes are fetched once, the first time "From Treatment plan" opens.
  useEffect(() => {
    if (scope !== 'tp' || tp || tpLoading) return;
    setTpLoading(true);
    api
      .get<TpPlanSummary>(`/contacts/${contactId}/tp-summary`)
      .then(setTp)
      .catch(() =>
        setTp({
          available: false,
          planId: null,
          signed: null,
          updatedAt: null,
          diagnoses: [],
          goals: [],
        }),
      )
      .finally(() => setTpLoading(false));
  }, [scope, tp, tpLoading, contactId]);

  useEffect(() => {
    if (!scope) return;
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setScope(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [scope]);

  const open = (s: DxScope) => {
    setSource(s);
    setQuery('');
    setScope(s);
  };
  const picked = (code: string) => value.some((d) => d.code === code);
  const toggle = (code: string, label: string) =>
    onChange((prev) =>
      prev.some((d) => d.code === code)
        ? prev.filter((d) => d.code !== code)
        : [...prev, { code, label, source: scope === 'tp' ? 'tp' : 'search' }],
    );

  const first = contactName.split(' ')[0] || contactName;
  const title = scope === 'tp' ? 'Diagnosis codes on Treatment Plan' : 'Suggested diagnosis codes';
  const sub =
    scope === 'tp' ? `From ${contactName}'s Treatment Plan.` : 'Probable codes for this note.';
  const groups = scope ? dxGroups(scope, query, concerns, tp?.diagnoses ?? [], value) : [];
  const tpEmpty =
    scope === 'tp' && !tpLoading && tp !== null && (tp.diagnoses.length === 0 || !tp.available);

  return (
    <div className="pn-card">
      <h3>Diagnosis codes (ICD-10)*</h3>
      <div className="pn-dx-source-toggle">
        <button
          type="button"
          className={`pn-dx-source-btn${source === 'tp' ? ' active' : ''}`}
          onClick={() => open('tp')}
        >
          From Treatment plan
        </button>
        <button
          type="button"
          className={`pn-dx-source-btn${source === 'all' ? ' active' : ''}`}
          onClick={() => open('all')}
        >
          Search diagnosis codes…
        </button>
      </div>
      <div className="pn-dx-selected-row">
        {value.map((d) => (
          <div key={d.code} className="pn-dx-selected-chip">
            <b>{d.code}</b>
            {d.label ? <> · {d.label}</> : null}
            <button
              type="button"
              className="pn-dx-selected-remove"
              aria-label={`Remove ${d.code}`}
              onClick={() => onChange((prev) => prev.filter((x) => x.code !== d.code))}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {scope && (
        <div
          className="pn-dx-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setScope(null);
          }}
        >
          <div className="pn-dx-modal" role="dialog" aria-modal="true" aria-label={title}>
            <div className="pn-dx-modal-head">
              <div>
                <h3>{title}</h3>
                <div className="pn-dx-modal-sub">{sub}</div>
              </div>
              <button
                type="button"
                className="pn-dx-modal-close"
                aria-label="Close"
                onClick={() => setScope(null)}
              >
                ×
              </button>
            </div>
            <input
              ref={searchRef}
              type="text"
              className="pn-chip-other"
              placeholder="Search ICD-10 codes..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="pn-dx-code-list">
              {scope === 'tp' && tpLoading && <div className="pn-dx-hint">Loading the plan…</div>}
              {tpEmpty && (
                <div className="pn-dx-hint">
                  {tp?.available
                    ? `No diagnosis on ${first}'s Treatment Plan yet. Use Search diagnosis codes instead.`
                    : "Couldn't reach the Treatment Plan module. Use Search diagnosis codes instead."}
                </div>
              )}
              {groups.map((g, gi) => (
                <div key={g.label ?? `g${gi}`}>
                  {gi > 0 && <div className="pn-dx-divider" />}
                  {g.label && <div className="pn-dx-group-label">{g.label}</div>}
                  {g.rows.map(([code, label]) => (
                    <div
                      key={code}
                      className={`pn-dx-code-row${picked(code) ? ' checked' : ''}`}
                      role="checkbox"
                      aria-checked={picked(code)}
                      tabIndex={0}
                      onClick={() => toggle(code, label)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle(code, label);
                        }
                      }}
                    >
                      <span className="pn-dx-code-checkbox">✓</span>
                      <span className="pn-dx-code-text">
                        {code}
                        {label ? ` · ${label}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
